import * as THREE from "three";
import { addErrorToOverlay, resetErrorOverlay } from 'modern-error-overlay'
import * as twgl from "twgl.js";
import { Pane } from 'tweakpane';
import Konva from "konva";
import { lru } from "tiny-lru";
import * as TOML from "smol-toml";

import { UserInterface } from "./user_interface.js"
import { Keyboard } from "./keyboard";
import { Mouse } from "./mouse";
import { loadFile, encodeToSharedArrayBuffer } from "./utils";

class ExternalModules {
  constructor() {
    this.keyboard = new Keyboard();
    this.mouse = new Mouse();
  }

  register(k, m) {
    this[k] = m
  }
}

class Renderer {
  constructor(voxel_screen) {
    this.voxel_screen = voxel_screen

    this.external_modules = new ExternalModules();

    this.canvas = document.getElementById("canvas");

    this.ui = new UserInterface(this);

    this.gl = this.canvas.getContext("webgl2", {antialias: true});
    this.start_time = new Date().getTime();
    this.frame_counter = 0;
    this.time = new Date().getTime();
    this.running = false;

    if (!this.gl) {
      console.error("WebGL2 is not available in your browser.");
      return;
    }

    this.resizeCanvas();

    this.uniforms = {
      time: 0.0,
      resolution: [window.innerWidth, window.innerHeight],
    };

    this.voxel_screen.setRenderer(this)
  }

  async init() {
    resetErrorOverlay();

    // Compile shaders and initialize buffers
    try {
      this.screen_renderer = await this.compileShaders('shaders/worldVertex.vert', 'shaders/worldRender.frag');
    } catch (err) {
      console.error("Shader compile failed:", err);
      if (!this.screen_renderer) {
        this.screen_renderer = await this.compileShaders('shaders/worldVertex.vert', 'shaders/fallback.frag');
      }
    }

    this.render([0.0]);
  }

  resizeCanvas() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    this.gl.viewport(0, 0, this.gl.canvas.width, this.gl.canvas.height);
  }

  async resolveIncludes(source, currentDir = "", visited = new Set()) {
    const includeRegex = /^[ \t]*#include\s+"(.+)"\s*$/gm;

    const resolveFile = async (filePath, parentDir) => {
      // normalize path
      const fullPath = parentDir + filePath;

      if (visited.has(fullPath)) {
        return "";
        // throw new Error(`Circular include detected: ${fullPath}`);
      }

      visited.add(fullPath);

      let fileSource;

      if (this._shaderCache.has(fullPath)) {
        fileSource = this._shaderCache.get(fullPath);
      } else {
        fileSource = await loadFile(fullPath);
        this._shaderCache.set(fullPath, fileSource);
      }

      // compute directory of included file
      const nextDir = fullPath.substring(0, fullPath.lastIndexOf("/") + 1);

      return await this.resolveIncludes(fileSource, nextDir, visited);
    };

    let match;

    while ((match = includeRegex.exec(source)) !== null) {
      const includePath = match[1];

      const includedSource = await resolveFile(includePath, currentDir);

      source =
        source.slice(0, match.index) +
        includedSource + "\n" +
        source.slice(includeRegex.lastIndex);

      includeRegex.lastIndex = 0;
    }

    return source;
  }

  resolveUnrollFor(src) {
    return src.replace(
      /#unroll_for\(\s*(\w+)\s*,\s*(\d+)\s*\)\s*\{([\s\S]*?)\}\s*#endunroll/g,
      (_, varName, count, body) => {
        let result = "";

        for (let i = 0; i < Number(count); i++) {
          const replaced = body.replace(
            new RegExp(`\\b${varName}\\b`, "g"),
            i
          );

          result += `
          {
              const int ${varName} = ${i};
              if (${varName} < numChunks) {
                  ${replaced}
              }
          }
          `;
        }

        return result;
      }
    );
  }

  async addVars(source, vars) {
    if (vars === undefined) {
      return source;
    }

    var header = "";
    for (const [k, type] of Object.entries(vars.types)) {
      const value = vars.params[k];

      var glslValue;

      switch (type) {
        case 'bool':
          glslValue = value ? '1' : '0';
          break;

        case 'int':
          glslValue = Math.floor(value).toString();
          break;

        case 'float':
          glslValue = Number(value).toFixed(6);
          glslValue = glslValue.replace(/\.?0+$/, '');
          if (!glslValue.includes('.')) {
            glslValue += '.0';
          }
          break;

        default:
          glslValue = value;
      }

      const macro = k
        .toUpperCase()
        .replaceAll(' ', '_');

      header += `#define ${macro} ${glslValue}\n`;
    }
    header += "\n"

    return header + source;
  }

  addVersionString(shaderSource) {
    return "#version 300 es\n\n" + shaderSource;
  }

  async resolveMacros(source, base, vars) {
    source = await this.addVars(source, vars);

    this._shaderCache = new lru(100);
    source = await this.resolveIncludes(source, base);
    source = this.resolveUnrollFor(source);

    return source;
  }

  async compileShaders(vertexFile, fragmentFile) {
    var vertexShaderSource = await loadFile(vertexFile);
    var fragmentShaderSource = await loadFile(fragmentFile);

    vertexShaderSource = await this.resolveMacros(vertexShaderSource, "shaders/");
    fragmentShaderSource = await this.resolveMacros(fragmentShaderSource, "shaders/", this.ui);

    fragmentShaderSource = this.addVersionString(fragmentShaderSource);

    let programInfo = twgl.createProgramInfo(this.gl, [
      vertexShaderSource,
      fragmentShaderSource,
    ],
    (err) => {
      const text = String(err);

      const lines = text.split("\n");

      const index = lines.findIndex(line => line.includes("^^^ ERROR"));

      if (index === -1) {
        console.log("No '^^^ ERROR' found");
        console.log(err);
        return;
      }

      const start = Math.max(0, index - 10);
      const end = Math.min(lines.length, index + 11);

      addErrorToOverlay({
        title: "Shader compilation error",
        message: lines.slice(index, index+1).join("\n").slice(11),
        codeFrame: lines.slice(start, end).join("\n")
      });

      // ErrorOverlay.reportBuildError({
      //   message: lines.slice(start, end).join("\n"),
      // });
    }
    );

    const arrays = {
      position: [-1, -1, 0, 3, -1, 0, -1, 3, 0],
    };
    let bufferInfo = twgl.createBufferInfoFromArrays(this.gl, arrays);

    return {
      vertexShaderSource: vertexShaderSource,
      fragmentShaderSource: fragmentShaderSource,
      programInfo: programInfo,
      program: programInfo.program,
      bufferInfo: bufferInfo,
    }
  }

  initTextureRenderer(renderer, width, height) {
    const gl = this.gl;
    gl.useProgram(renderer.program);

    const posLoc = gl.getAttribLocation(renderer.program, "a_position");
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const rbo = gl.createRenderbuffer();
    gl.bindRenderbuffer(gl.RENDERBUFFER, rbo);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.R32UI, width, height);

    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferRenderbuffer(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.RENDERBUFFER,
      rbo
    );

    renderer.fbo = fbo;
    renderer.width = width;
    renderer.height = height;

    renderer.readFormat = gl.RED_INTEGER;
    renderer.readType = gl.UNSIGNED_INT;

    renderer.pixelBuffer = new Uint32Array(width * height);
  }

  setUniforms(renderer, uniforms) {
    this.gl.useProgram(renderer.program);
    twgl.setUniforms(renderer.programInfo, uniforms);
  }

  resizeCanvas(canvas) {
    if (canvas === undefined) {
      return;
    }
    const dpr = window.devicePixelRatio || 1;

    const width = canvas.clientWidth * dpr;
    const height = canvas.clientHeight * dpr;

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
  }

  render(deltas, camera) {
    twgl.resizeCanvasToDisplaySize(this.gl.canvas);
    this.gl.viewport(0, 0, this.gl.canvas.width, this.gl.canvas.height);

    this.gl.useProgram(this.screen_renderer.program);
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT);
    twgl.setBuffersAndAttributes(this.gl, this.screen_renderer.programInfo, this.screen_renderer.bufferInfo);
    twgl.drawBufferInfo(this.gl, this.screen_renderer.bufferInfo, this.gl.TRIANGLES);

    // this.show_ui = true;
    this.ui.pane.hidden = !this.show_ui;
    if(this.show_ui) {
      this.ui.render(deltas, camera);
    }
  }

  renderTexture(renderer) {
    /*
      This function works, I just don't know exactly how I want to
      use it. The idea was to add a shader program to each voxel,
      which either contains a position and can be moved or is a space
      in the screen where I can change the color.
      This was to get around the bottleneck of addVoxel
      (which is a bottleneck because it defines where voxels are drawn, which should not be a CPU operation honestly).

      An alternative would be to cache the voxel changes, use the logic
      in addVoxel and port it to the GPU. It's then a stage before
      the raymarching, generating a texture we can immediately use.
      However, this should be done later...
    */
    const gl = this.gl;

    gl.useProgram(renderer.program);

    gl.bindFramebuffer(gl.FRAMEBUFFER, renderer.fbo);
    gl.viewport(0, 0, renderer.width, renderer.height);

    gl.disable(gl.DEPTH_TEST);

    const clearVal = new Int32Array([0, 0, 0, 0]);
    gl.clearBufferuiv(gl.COLOR, 0, clearVal);

    twgl.setBuffersAndAttributes(gl, renderer.programInfo, renderer.bufferInfo);
    twgl.drawBufferInfo(gl, renderer.bufferInfo, this.gl.TRIANGLES);

    // Read back (SPEC-COMPLIANT)
    gl.readBuffer(gl.COLOR_ATTACHMENT0);
    gl.readPixels(
      0,
      0,
      renderer.width,
      renderer.height,
      renderer.readFormat,
      renderer.readType,
      renderer.pixelBuffer
    );

    return renderer.pixelBuffer;
  }

  stopRenderLoop() {
    this.running = false;
  }

  gameLogic(delta) {
    function openPanel(id) {
      setTimeout(() => {
        const el = document.getElementById(id);
        if (!el) return;

        el.classList.remove("hidden");

        el.classList.remove("animate-unfold");
        el.classList.remove("animate-fold");
        void el.offsetWidth;
        el.classList.add("animate-unfold");
      }, 500);
    }

    function closePanel(id, gv, newSelect) {
      const el = document.getElementById(id);
      if (!el) return;

      if (el.classList.contains("hidden")) return;

      el.classList.remove("animate-fold");
      el.classList.remove("animate-unfold");
      void el.offsetWidth;
      el.classList.add("animate-fold");

      setTimeout(() => {
        el.classList.add("hidden");

        gv.camPatht = 0;
        gv.moveFrom = { ...gv.selectionCamParams[gv.selected] };
        gv.selected = newSelect;
        gv.moveTo = { ...gv.selectionCamParams[gv.selected] };
      }, 1100);
    }

    if (this.gamevars === undefined) {
      const ar = this.camera.aspect_ratio;
      const radius = 300 + 70 * 1.7 / ar;

      this.gamevars = {
        selected: 0,

        hitBox1: new THREE.Box3(
          new THREE.Vector3(-76, 444, -32),
          new THREE.Vector3(-57, 463, -29)
        ),

        hitBox2: new THREE.Box3(
          new THREE.Vector3(55, 444, -32),
          new THREE.Vector3(74, 463, -29)
        ),

        hitBox3: new THREE.Box3(
          new THREE.Vector3(-51, 527, 38),
          new THREE.Vector3(-32, 546, 41)
        ),

        hitBox4: new THREE.Box3(
          new THREE.Vector3(80, 527, 38),
          new THREE.Vector3(99, 546, 41)
        ),

        hitBox5: new THREE.Box3(
          new THREE.Vector3(-50, 442, -30),
          new THREE.Vector3(41, 469, -24)
        ),

        hitBox6: new THREE.Box3(
          new THREE.Vector3(-21, 522, 37),
          new THREE.Vector3(68, 551, 41)
        ),

        camPatht: 0.0,
        yStart: 872,
        yEnd: 120,
        thetaStart: Math.PI/2,
        thetaEnd: 17*Math.PI - 0.4,

        selectionCamParams: [
          {
            radius: radius,
            theta: Math.PI,
            y: 482,
            pitch: -0.2,
          },
          {
            radius: radius,
            theta: 0.0,
            y: 522,
            pitch: -0.2,
          },
          {
            radius: radius*0.8,
            theta: -3*Math.PI - 0.3,
            y: 170,
            pitch: -50.0,
          },
          {
            radius: radius,
            theta: 2*Math.PI + Math.PI/2,
            y: 872,
            pitch: -0.2,
          },
        ],
        stops: [
           0.5,
           1.0
        ],
      }

      this.camera.disableControls();
      this.gamevars.moveFrom = this.gamevars.selectionCamParams[this.gamevars.selected];
      this.gamevars.moveTo = this.gamevars.selectionCamParams[this.gamevars.selected];

      document.getElementById("open-debug").addEventListener("click", () => {
        this.show_ui = true;
      });

      document.querySelectorAll(".minimize-btn").forEach((button) => {
          let isAnimating = false;

          button.addEventListener("click", () => {
              if (isAnimating) return;

              const content = button.closest(".backdrop").querySelector(".content");
              if (!content) return;

              isAnimating = true;

              content.classList.toggle("minimized");
          });

          button
              .closest(".backdrop")
              .querySelector(".content")
              .addEventListener("animationend", () => {
                  isAnimating = false;
              });
      });

      const scrollArea = document.getElementById("scroll-area");
      const vh = window.innerHeight;
      const scroll = window.scrollY;
      const scrollMax = document.documentElement.scrollHeight - vh;
      this.gamevars.camPatht = scroll / scrollMax;
    }

    const gv = this.gamevars;

    // if(this.external_modules.mouse.isJustPressed(0)) {
    //   const projects = document.getElementById("projects");
    //   if (!this.external_modules.mouse.inElement(projects)) {
    //     closePanel("projects", gv, 0);
    //   }

    //   const aboutme = document.getElementById("aboutme");
    //   if (!this.external_modules.mouse.inElement(aboutme)) {
    //     closePanel("aboutme", gv, 1);
    //   }
    // }

    // function hitbox1Pressed(gv) {
    //   gv.camPatht = 0;
    //   gv.moveFrom = { ...gv.selectionCamParams[gv.selected] };
    //   gv.selected = gv.selected == 0 ? 1 : 0;
    //   gv.moveTo = { ...gv.selectionCamParams[gv.selected] };
    //   gv.moveTo.theta += 2 * Math.PI;
    // }
    // function hitbox2Pressed(gv) {
    //   gv.camPatht = 0;
    //   gv.moveFrom = { ...gv.selectionCamParams[gv.selected] };
    //   gv.selected = gv.selected == 0 ? 1 : 0;
    //   gv.moveTo = { ...gv.selectionCamParams[gv.selected] };
    // }
    // function hitbox3Pressed(gv) {
    //   gv.camPatht = 0;
    //   gv.moveFrom = { ...gv.selectionCamParams[gv.selected] };
    //   gv.selected = gv.selected == 0 ? 1 : 0;
    //   gv.moveTo = { ...gv.selectionCamParams[gv.selected] };
    //   gv.moveTo.theta -= 2*Math.PI
    // }
    // function hitbox4Pressed(gv) {
    //   gv.camPatht = 0;
    //   gv.moveFrom = { ...gv.selectionCamParams[gv.selected] };
    //   gv.selected = gv.selected == 0 ? 1 : 0;
    //   gv.moveTo = { ...gv.selectionCamParams[gv.selected] };
    // }


    // const temp = new THREE.Vector3();
    // gv.highlighted = -1;
    // if(gv.selected == 0) {
    //   if (this.external_modules.mouse.justSwiped("left")) {
    //     hitbox1Pressed(gv);
    //   } else if (this.external_modules.mouse.justSwiped("right")) {
    //       hitbox2Pressed(gv);
    //   } else if (ray.intersectBox(gv.hitBox1, temp)) {
    //     if(this.external_modules.mouse.isJustPressed(0)) {
    //       hitbox1Pressed(gv);
    //     }
    //     gv.highlighted = 0;
    //   } else if (ray.intersectBox(gv.hitBox2, temp)) {
    //     if(this.external_modules.mouse.isJustPressed(0)) {
    //       hitbox2Pressed(gv);
    //     }
    //     gv.highlighted = 1;
    //   } else if (ray.intersectBox(gv.hitBox5, temp)) {
    //     if(this.external_modules.mouse.isJustPressed(0)) {
    //       gv.camPatht = 0;
    //       gv.moveFrom = { ...gv.selectionCamParams[gv.selected] };
    //       gv.selected = 2;
    //       gv.moveTo = { ...gv.selectionCamParams[gv.selected] };
    //     }
    //     gv.highlighted = 2;
    //   }
    // }
    // else if(gv.selected == 1) {
    //   if (this.external_modules.mouse.justSwiped("left")) {
    //     hitbox4Pressed(gv);
    //   } else if (this.external_modules.mouse.justSwiped("right")) {
    //     hitbox3Pressed(gv);
    //   } else if (ray.intersectBox(gv.hitBox3, temp)) {
    //     if(this.external_modules.mouse.isJustPressed(0)) {
    //       hitbox3Pressed(gv);
    //     }
    //     gv.highlighted = 3;
    //   } else if (ray.intersectBox(gv.hitBox4, temp)) {
    //     if(this.external_modules.mouse.isJustPressed(0)) {
    //       hitbox4Pressed(gv);
    //     }
    //     gv.highlighted = 4;
    //   } else if (ray.intersectBox(gv.hitBox6, temp)) {
    //     if(this.external_modules.mouse.isJustPressed(0)) {
    //       gv.camPatht = 0;
    //       gv.moveFrom = { ...gv.selectionCamParams[gv.selected] };
    //       gv.selected = 3;
    //       gv.moveTo = { ...gv.selectionCamParams[gv.selected] };
    //     }
    //     gv.highlighted = 5;
    //   }
    // }

    const tspeed = Math.abs(gv.moveFrom.theta - gv.moveTo.theta) * Math.PI / 10.0;
    if(tspeed > 0.0) {
      gv.camPatht += delta/tspeed;
    }
    gv.camPatht = THREE.MathUtils.clamp(gv.camPatht, 0.0, 1.0);

    if (gv.selected == 2 && gv.camPatht >= 1.0 && document.getElementById("projects").classList.contains("hidden")) {
      openPanel("projects");
    }
    if (gv.selected == 3 && gv.camPatht >= 1.0 && document.getElementById("aboutme").classList.contains("hidden")) {
      openPanel("aboutme");
    }

    const center = new THREE.Vector3(3, 533, -7);

    // const radius = THREE.MathUtils.lerp(gv.moveFrom.radius, gv.moveTo.radius, gv.camPatht);
    // const theta = THREE.MathUtils.lerp(gv.moveFrom.theta, gv.moveTo.theta, gv.camPatht);
    // const y = THREE.MathUtils.lerp(gv.moveFrom.y, gv.moveTo.y, gv.camPatht);
    // const pitch = THREE.MathUtils.lerp(gv.moveFrom.pitch, gv.moveTo.pitch, gv.camPatht);

    const ar = this.camera.aspect_ratio;
    // const radius = 300;
    const radius = 300 + 70 * 1.1 / ar;
    const theta = THREE.MathUtils.lerp(gv.thetaStart, gv.thetaEnd, gv.camPatht);
    const y = THREE.MathUtils.lerp(gv.yStart, gv.yEnd, gv.camPatht);

    const scroll = window.scrollY;
    const scrollMax = document.documentElement.scrollHeight - window.innerHeight;
    const targetT = scroll / scrollMax;
    gv.camPatht = THREE.MathUtils.lerp(gv.camPatht, targetT, 0.20);

    const pitch = -0.2;
    const pos = new THREE.Vector3();
    pos.x = center.x + Math.sin(theta) * radius;
    pos.y = y;
    pos.z = center.z + Math.cos(theta) * radius;

    const dir = center.clone().sub(pos);
    dir.y = pitch;
    dir.normalize();


    if(!this.camera.controls_enabled) {
      this.camera.setPosition(pos);
      this.camera.setDirection(dir);
    }


    const ray = this.camera.makeRay(this.external_modules.mouse.position);
    this.setUniforms(this.screen_renderer, {
      gameSelected: gv.selected,
      mouseRay: { origin: ray.origin.toArray(), direction: ray.direction.toArray() },
      // gameHighlighted: gv.highlighted
    });
  }

  async startRenderLoop(camera, lua_runner_worker) {
    this.running = true;
    this.camera = camera;
    this.voxel_screen.setCamera(camera);

    // lua_runner_worker.onmessage = async (event) => {
    //   if(event.data.type == "render") {
    //     for (const message of event.data.messages) {
    //       let method;
    //       if(message.class in this) {
    //         method = this[message.class][message.fun];
    //       } else if(message.class in this.external_modules) {
    //         method = this.external_modules[message.class][message.fun];
    //       } else {
    //         console.log("error!")
    //       }

    //       if (typeof method === 'function') {
    //         const result = method.call(this[message.class], message.params);

    //         // Check if the result is a Promise (i.e., it's awaitable)
    //         if (result instanceof Promise) {
    //           await result;
    //         }
    //       }
    //     }

    //     requestAnimationFrame(renderLoop);
    //   }
    //   else if(event.data.type == "get_from_main_thread") {
    //     const message = event.data.message;

    //     let method;
    //     if(message.class in this) {
    //       method = this[message.class][message.fun];
    //     } else if(message.class in this.external_modules) {
    //       method = this.external_modules[message.class][message.fun];
    //     } else {
    //       console.log("error!")
    //     }

    //     if (typeof method === 'function') {
    //       const result = method.call(this.external_modules[message.class], message.params);

    //       // Check if the result is a Promise (i.e., it's awaitable)
    //       if (result instanceof Promise) {
    //         await result;
    //       }

    //       encodeToSharedArrayBuffer(result, event.data.buffer);
    //     }
    //   }
    // }

    // function lua_runner_render(dt) {
    //   lua_runner_worker.postMessage({ type: "render", data: { dt } })
    // }

    const deltas = Array();
    const renderLoop = async () => {
      const t_frame = performance.now();

      const t_now = new Date().getTime();
      const delta = (t_now - this.time) / 1e3;
      deltas.push(delta);
      while (deltas.length > 20) deltas.shift();
      this.time = t_now;
      this.uniforms.time = this.time - this.start_time;

      this.setUniforms(this.screen_renderer, this.uniforms);

      this.setUniforms(
        this.screen_renderer,
        this.voxel_screen.getUniformsForScreen()
      );

      camera.updateMovement();

      this.setUniforms(this.screen_renderer, camera.getUniforms());

      this.render(deltas, camera);

      this.gameLogic(delta);

      this.external_modules.keyboard.endFrame();
      this.external_modules.mouse.endFrame();

      // const t7 = performance.now();
      // if (this.running) lua_runner_render(delta);
      // console.log("lua_runner_render:", (performance.now() - t7).toFixed(3), "ms");

      // console.log("FRAME TOTAL:", (performance.now() - t_frame).toFixed(3), "ms");

      requestAnimationFrame(renderLoop);
    };
    renderLoop();
  }
}

export default Renderer;
